import { Suspense, useEffect, useState, type ReactNode } from "react";
import useSWR, { SWRConfig } from "swr";
import {
  BrowserRouter,
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Avatar,
  Badge,
  CommandDialog,
  type CommandItem,
  Divider,
  HStack,
  NavTree,
  type NavTreeItem,
  type NavTreeItemState,
  type NavTreeSection,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ThemeToggle,
  ThemeProvider,
  Tooltip,
  TooltipProvider,
  ToastProvider,
  ConfirmProvider,
} from "@novel-isr/ui";
import {
  FiActivity,
  FiAlertTriangle,
  FiBell,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiDatabase,
  FiLogOut,
  FiMenu,
  FiSearch,
  FiServer,
  FiUser,
  FiX,
} from "react-icons/fi";
import {
  ADMIN_ONLY_NAV_ITEM_IDS,
  ADMIN_ROUTES,
  COMMAND_ITEMS,
  NAV_SECTIONS,
  ROUTE_PAGES,
  type AdminCommandItem,
} from "./routes";
import { LoadingPage } from "./components/LoadingPage/LoadingPage";
import { ErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";
import {
  LIVE_REFRESH_INTERVAL_STATIC_MS,
  liveRefreshInterval,
  useLiveMode,
} from "./lib/use-live-mode";
import {
  adminJson,
  getAdminUser,
  hasAdminSessionMarker,
  logoutAdmin,
} from "./lib/admin-api";

const DEFAULT_EXPANDED_NAV_IDS = [
  "delivery-control",
  "runtime-config",
  "insight-domain",
  "identity-domain",
  "governance-domain",
];
const COLLAPSED_NAV_SECTIONS = buildCollapsedNavSections(NAV_SECTIONS);

interface RuntimeHealth {
  ok: boolean;
  dependencies?: {
    database?: {
      status: "available" | "degraded";
    };
  };
}

interface MockState {
  dataSource: "mock" | "business";
  routeCount: number;
}

interface ObservabilitySummary {
  totals: {
    events: number;
    errors: number;
    pageViews: number;
  };
}

/** 顶栏通知用的精简快照。
 * 状态卡片已经下沉到 dashboard KPI，这里只保留 NotificationPopover 需要的字段。 */
interface RuntimeSnapshot {
  healthOk: boolean;
  databaseStatus: "available" | "degraded";
  dataSource: "mock" | "business";
  mockRoutes: number;
  observabilityEvents: number;
  observabilityErrors: number;
  /** 数量 + 距离最早过期还剩多少天，用于顶栏 build token 即将过期提醒 */
  buildTokensExpiringCount: number;
  buildTokensSoonestExpiringDays: number | null;
}

interface ExpiringBuildToken {
  id: string;
  label: string;
  expiresInDays: number;
  thresholdDays: number;
}

const fetcher = <T,>(url: string) => adminJson<T>(url);
// 给会被非管理员撞 403/401 的轮询用 —— 静默错误，不弹全局 toast。
// 浏览器 network log 仍会有一行 401（这层 JS 改不动），但至少没有 UX 噪音。
const silentFetcher = <T,>(url: string) => adminJson<T>(url, { silent: true });

export function App() {
  return (
    <ThemeProvider defaultTheme="system">
      <TooltipProvider delayDuration={180}>
        <ToastProvider position="top-right">
          <ConfirmProvider>
            {/* SWR 全局默认：管理后台是长驻 tab，工程师 alt-tab / 切窗口
                十几次很常见——默认的 revalidateOnFocus 会让所有 SWR fetcher
                在每次回 tab 时同时重发，再叠上各页 5s 轮询、admin-server 偶发
                503，就形成"自动请求风暴"。这里关掉 focus/reconnect 兜底重发
                （需要的页面自己开），并禁掉 4xx/5xx 的退避重试——服务端给了
                确定回复，再打也是同样的错。 */}
            <SWRConfig
              value={{
                revalidateOnFocus: false,
                revalidateOnReconnect: false,
                shouldRetryOnError: false,
                dedupingInterval: 2_000,
              }}
            >
              <BrowserRouter>
                {/* 兜底渲染异常：抓住任何子树同步抛错，渲染 ErrorFallback。
                    数据请求 / 业务失败走 admin-api 的全局 toast，不进这层。 */}
                <ErrorBoundary>
                  <AdminRouter />
                </ErrorBoundary>
              </BrowserRouter>
            </SWRConfig>
          </ConfirmProvider>
        </ToastProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}


function AdminRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // 没登录就别让 AdminShell 挂载——AdminShell 内的 SWR 全是 admin-only
  // 端点，挂上去就先发一轮 401，再被 useEffect 异步跳转到 /login，徒增噪音。
  // 这里同步跳转，第一次渲染就不挂 AdminShell 子树，浏览器 console 干净。
  useEffect(() => {
    if (location.pathname !== "/login" && !hasAdminSessionMarker()) {
      navigate("/login", { replace: true });
    }
  }, [location.pathname, navigate]);

  if (location.pathname === "/login") {
    const LoginPage = ROUTE_PAGES.login;
    return (
      <main className="auth-layout">
        <Suspense fallback={<LoadingPage label="正在打开登录页..." />}>
          <LoginPage />
        </Suspense>
      </main>
    );
  }
  // 没 session marker 时先空渲染——上面的 useEffect 这一帧之后会把路由切到
  // /login。空渲染期 AdminShell 不挂载，里面的 SWR fetcher 也就不会发 401。
  if (!hasAdminSessionMarker()) return null;
  return <AdminShell />;
}

function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const storedValue = window.localStorage.getItem("admin-sidebar-collapsed");
      if (storedValue !== null) return storedValue === "true";
      return window.matchMedia("(max-width: 1024px)").matches;
    } catch {
      return false;
    }
  });
  const [commandOpen, setCommandOpen] = useState(false);
  const runtimeSnapshot = useRuntimeSnapshot();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (String(event.key ?? "").toLowerCase() !== "k") return;
      if (!event.metaKey && !event.ctrlKey) return;
      event.preventDefault();
      event.stopPropagation();
      setCommandOpen(true);
    };
    document.addEventListener("keydown", onKeyDown, { capture: true });
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  const updateSidebarCollapsed = (value: boolean) => {
    setSidebarCollapsed(value);
    try {
      window.localStorage.setItem("admin-sidebar-collapsed", String(value));
    } catch {
      // localStorage 受限时只保留当前会话状态。
    }
  };

  const runCommand = (item: CommandItem) => {
    setCommandOpen(false);
    const target = (item as AdminCommandItem).target;
    if (typeof target === "string") navigate(target);
  };

  const closeSidebarOnNarrowScreen = () => {
    if (window.matchMedia("(max-width: 1024px)").matches) {
      updateSidebarCollapsed(true);
    }
  };
  // 当前用户的 scopes 决定 sidebar 里 admin-only 项是否可见。
  // 用 SWR 缓存，每次切路由不重复请求；权限刚好失败时（cold start / 401）按"未授予"渲染。
  const { data: permissions } = useSWR<{
    role: string;
    scopes: string[];
    requiresTwoFactorEnroll?: boolean;
  }>("/api/auth/permissions", fetcher);
  const isAdmin =
    !!permissions &&
    (permissions.scopes.includes("*") || permissions.scopes.includes("identity:rbac:manage"));
  const requiresTwoFactor = permissions?.requiresTwoFactorEnroll === true;
  const filteredSections = filterAdminOnlyNav(NAV_SECTIONS, isAdmin);
  const filteredCollapsed = filterAdminOnlyNav(COLLAPSED_NAV_SECTIONS, isAdmin);
  const activeNavId = findActiveNavId(filteredSections, location.pathname);
  const navSections = sidebarCollapsed ? filteredCollapsed : filteredSections;

  return (
    <div className={`layout ${sidebarCollapsed ? "layout-collapsed" : ""}`}>
      <aside className="sidebar">
        <Tooltip
          label={sidebarCollapsed ? "展开菜单" : "折叠菜单"}
          side="right"
          tone="dark"
        >
          <button
            className="sidebar-toggle"
            type="button"
            aria-label={sidebarCollapsed ? "展开菜单" : "折叠菜单"}
            aria-expanded={!sidebarCollapsed}
            onClick={() => updateSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        </Tooltip>
        <div className="brand">
          <HStack justify="between" align="center">
            <img className="brand-logo" src="/logo.svg" alt="" />
            <div className="brand-copy">
              <p className="brand-kicker">新优书网</p>
              <h1 className="logo">管理后台</h1>
              <p className="brand-subtitle" title="运行时配置与运营中心">
                运行时配置与运营中心
              </p>
            </div>
            <Badge colorScheme="success">开发</Badge>
          </HStack>
        </div>
        <div className="nav">
          <NavTree
            aria-label="后台主导航"
            sections={navSections}
            activeId={activeNavId}
            collapsed={sidebarCollapsed}
            defaultExpandedIds={DEFAULT_EXPANDED_NAV_IDS}
            renderLink={(item, content, state) =>
              renderNavLink(item, content, state, closeSidebarOnNarrowScreen)
            }
          />
        </div>
      </aside>
      <button
        className="sidebar-scrim"
        type="button"
        hidden={sidebarCollapsed}
        aria-label="关闭菜单"
        onClick={() => updateSidebarCollapsed(true)}
      />
      {/* hamburger 渲染在 layout 直接子级 —— 不放进 .topbar 是因为：
       * topbar 在桌面端有 backdrop-filter / z-index，会建立 stacking context，
       * 把内部 fixed 子元素的 z-index 困在 topbar 层里，
       * 移动端 drawer (z:60) 反而盖住 hamburger，无法关闭。
       * 放在 .layout 直接子级 = 与 .sidebar 同层，z-index 70 真正生效。 */}
      <Tooltip
        label={sidebarCollapsed ? "展开菜单" : "关闭菜单"}
        side="bottom"
        tone="dark"
      >
        <button
          type="button"
          className="topbar-menu-btn"
          aria-label={sidebarCollapsed ? "展开菜单" : "关闭菜单"}
          aria-expanded={!sidebarCollapsed}
          onClick={() => updateSidebarCollapsed(!sidebarCollapsed)}
        >
          {sidebarCollapsed ? <FiMenu /> : <FiX />}
        </button>
      </Tooltip>
      <main className="content">
        <header className="topbar">
          <HStack className="topbar-title" gap="3" align="center">
            <div>
              <p className="eyebrow">新优书网</p>
              <h2 title="运行配置中心">运行配置中心</h2>
            </div>
          </HStack>
          <div className="topbar-search">
            <button
              className="command-trigger"
              type="button"
              aria-label="搜索模块"
              onClick={() => setCommandOpen(true)}
            >
              <FiSearch aria-hidden="true" />
              <b>搜索模块 / 路径 / 文案 key</b>
              <kbd>⌘K</kbd>
            </button>
          </div>
          <HStack className="topbar-actions" gap="3" align="center">
            <ThemeToggle
              className="admin-theme-toggle"
              labels={{ light: "亮", dark: "暗", system: "跟随系统" }}
            />
            <NotificationPopover snapshot={runtimeSnapshot} />
            <AccountPopover />
          </HStack>
        </header>
        {requiresTwoFactor && location.pathname !== "/account" && (
          // admin 角色但还没绑 2FA：所有 /api/admin/* 写操作服务端会 403 two_factor_required，
          // 在这里给一个引导 banner，省得用户点了半天才知道为啥都不行
          <div
            role="alert"
            style={{
              padding: "10px 16px",
              margin: "12px 24px 0",
              borderRadius: 8,
              background: "rgba(255, 165, 0, 0.12)",
              border: "1px solid rgba(255, 165, 0, 0.4)",
              color: "var(--admin-text)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <span>
              <strong>必须绑定 2FA</strong> ——
              admin 角色还没启用双因素认证，新建用户 / 改密码 / 撤销 build token 等敏感操作都会被服务端拒绝。
            </span>
            <Link
              to="/account"
              style={{
                whiteSpace: "nowrap",
                padding: "6px 14px",
                borderRadius: 6,
                background: "var(--ui-color-brand-500)",
                color: "#fff",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              前往账号设置
            </Link>
          </div>
        )}
        <div className="workspace">
          {/* 路由切换时模块 lazy chunk 还没到的占位；这里再嵌一层 ErrorBoundary，
              单个模块崩溃时不会把整个 admin shell 一起拽下来 */}
          <ErrorBoundary>
            <Suspense fallback={<LoadingPage compact label="正在加载模块..." />}>
              <Routes>
                {ADMIN_ROUTES.map(({ path, Page }) => (
                  <Route key={path} path={path} element={<Page />} />
                ))}
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>

      <CommandDialog
        isOpen={commandOpen}
        onOpenChange={setCommandOpen}
        items={COMMAND_ITEMS}
        title="全局搜索"
        shortcutLabel="⌘K"
        placeholder="搜索模块、路径、SEO、i18n key 或接口"
        emptyText="没有匹配结果"
        onSelect={runCommand}
      />
    </div>
  );
}


function renderNavLink(
  item: NavTreeItem,
  content: ReactNode,
  state: NavTreeItemState,
  onSelect: () => void,
) {
  if (!item.href) return content;
  const link = (
    <NavLink
      key={item.id}
      to={item.href}
      title={item.description ? `${item.label} / ${item.description}` : item.label}
      className={state.className}
      aria-current={state.active ? "page" : undefined}
      data-active={state.active || undefined}
      onClick={onSelect}
    >
      {content}
    </NavLink>
  );

  if (!state.collapsed) return link;

  return (
    <Tooltip
      key={item.id}
      label={item.description ? `${item.label} · ${item.description}` : item.label}
      side="right"
      align="center"
      tone="dark"
    >
      {link}
    </Tooltip>
  );
}

function flattenNavItems(sections: readonly NavTreeSection[]) {
  const leaves: Array<{ section: NavTreeSection; item: NavTreeItem }> = [];
  for (const section of sections) {
    collectNavLeaves(section, section.items, leaves);
  }
  return leaves;
}

function buildCollapsedNavSections(
  sections: readonly NavTreeSection[],
): NavTreeSection[] {
  return sections.map((section) => ({
    ...section,
    items: flattenSectionLeaves(section.items).map((item) => ({
      ...item,
      children: undefined,
    })),
  }));
}

/**
 * 把 ADMIN_ONLY_NAV_ITEM_IDS 列出的 item 从 nav tree 里剔掉（递归）。
 * isAdmin 为 true 时直接放行；非 admin 看不到入口，万一有人手输 URL 进去
 * 也会被 UsersPage 自己的 permissions 校验挡掉。
 */
function filterAdminOnlyNav(
  sections: readonly NavTreeSection[],
  isAdmin: boolean,
): NavTreeSection[] {
  if (isAdmin) return [...sections];
  const filterItems = (items: readonly NavTreeItem[]): NavTreeItem[] =>
    items
      .filter((item) => !ADMIN_ONLY_NAV_ITEM_IDS.has(item.id))
      .map((item) =>
        item.children ? { ...item, children: filterItems(item.children) } : item,
      );
  return sections
    .map((section) => ({ ...section, items: filterItems(section.items) }))
    .filter((section) => section.items.length > 0);
}

function flattenSectionLeaves(items: readonly NavTreeItem[]): NavTreeItem[] {
  const leaves: NavTreeItem[] = [];
  for (const item of items) {
    if (item.href) leaves.push(item);
    if (item.children) leaves.push(...flattenSectionLeaves(item.children));
  }
  return leaves;
}

function collectNavLeaves(
  section: NavTreeSection,
  items: readonly NavTreeItem[],
  leaves: Array<{ section: NavTreeSection; item: NavTreeItem }>,
) {
  for (const item of items) {
    if (item.href) leaves.push({ section, item });
    if (item.children) collectNavLeaves(section, item.children, leaves);
  }
}

function findActiveNavId(
  sections: readonly NavTreeSection[],
  pathname: string,
): string | undefined {
  let exact: string | undefined;
  let prefix: { id: string; length: number } | undefined;
  for (const { item } of flattenNavItems(sections)) {
    if (!item.href) continue;
    if (item.href === pathname) {
      exact = item.id;
      break;
    }
    if (
      item.href !== "/" &&
      pathname.startsWith(`${item.href}/`) &&
      (!prefix || item.href.length > prefix.length)
    ) {
      prefix = { id: item.id, length: item.href.length };
    }
  }
  return exact ?? prefix?.id ?? (pathname === "/" ? "overview" : undefined);
}

function useRuntimeSnapshot(): RuntimeSnapshot {
  // 顶栏通知 bell 默认不轮询 —— 跟 dashboard 一样，靠 SWR
  // `revalidateOnFocus` 切回 tab 时刷新就够。用户开了「实时」开关再走轮询，
  // 跟 dashboard 自己的节奏一致（health 60s、mock 60s、observability 60s）。
  const [liveMode] = useLiveMode();
  const interval = liveRefreshInterval(liveMode, LIVE_REFRESH_INTERVAL_STATIC_MS);
  const { data: health, error: healthError } = useSWR<RuntimeHealth>(
    "/health/ready",
    fetcher,
    { refreshInterval: interval },
  );
  const { data: mock } = useSWR<MockState>("/api/mock/admin/state", fetcher, {
    refreshInterval: interval,
  });
  const { data: observability } = useSWR<ObservabilitySummary>(
    "/api/observability/summary",
    fetcher,
    { refreshInterval: interval },
  );

  // 先拿 permissions 决定是否要发这条 admin-only 的轮询。SWR 会按 cache key
  // 跟 AdminShell 共享同一份请求结果，不会重复打。
  const { data: permissions } = useSWR<{ role: string; scopes: string[] }>(
    "/api/auth/permissions",
    silentFetcher,
    { onError: () => undefined, shouldRetryOnError: false },
  );
  const canSeeBuildTokens =
    !!permissions &&
    (permissions.scopes.includes("*") ||
      permissions.scopes.includes("identity:rbac:manage"));

  // build token 过期提醒：低频，每 5 分钟一次足够；只有 admin 才发请求 ——
  // 非 admin 拿到 null key SWR 不会触发 fetch，浏览器 console 也不会有 401。
  // silent fetcher 双保险：万一 admin 自己的 session 过期了，错误也不弹 toast
  // （让 adminFetch 的 401 重试 + handleSessionExpired 兜底跳登录）。
  const { data: expiringTokens } = useSWR<{ items: ExpiringBuildToken[] }>(
    canSeeBuildTokens ? "/api/admin/build-tokens/expiring" : null,
    silentFetcher,
    {
      refreshInterval: 5 * 60 * 1000,
      onError: () => undefined,
      shouldRetryOnError: false,
    },
  );

  const healthOk = Boolean(health?.ok) && !healthError;
  const databaseStatus = health?.dependencies?.database?.status ?? "degraded";
  const dataSource = mock?.dataSource ?? "mock";
  const mockRoutes = mock?.routeCount ?? 0;
  const observabilityEvents = observability?.totals.events ?? 0;
  const observabilityErrors = observability?.totals.errors ?? 0;
  const expiringList = expiringTokens?.items ?? [];
  const buildTokensExpiringCount = expiringList.length;
  const buildTokensSoonestExpiringDays =
    expiringList.length > 0
      ? expiringList.reduce(
          (min, t) => (t.expiresInDays < min ? t.expiresInDays : min),
          expiringList[0]!.expiresInDays,
        )
      : null;

  return {
    healthOk,
    databaseStatus,
    dataSource,
    mockRoutes,
    observabilityEvents,
    observabilityErrors,
    buildTokensExpiringCount,
    buildTokensSoonestExpiringDays,
  };
}

/**
 * 关闭过的通知 id 持久化在 localStorage，刷新页面 / 切路由都不会"复活"。
 * 每条通知的 id 是稳定字符串（health / data-source / observability），不随 snapshot 数值变。
 */
const NOTIFICATION_DISMISSED_KEY = "admin-notifications-dismissed";

function readDismissedIds(): Set<string> {
  try {
    const raw = window.localStorage.getItem(NOTIFICATION_DISMISSED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? new Set(parsed.filter((x) => typeof x === "string")) : new Set();
  } catch {
    return new Set();
  }
}

function writeDismissedIds(ids: Set<string>): void {
  try {
    window.localStorage.setItem(NOTIFICATION_DISMISSED_KEY, JSON.stringify([...ids]));
  } catch {
    // localStorage 满 / 隐身模式禁写：忽略，最多一次会话内丢失
  }
}

function NotificationPopover({ snapshot }: { snapshot: RuntimeSnapshot }) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => readDismissedIds());

  const allNotifications = [
    {
      id: "health",
      icon: snapshot.healthOk ? FiCheckCircle : FiAlertTriangle,
      title: snapshot.healthOk ? "配置服务在线" : "配置服务降级",
      meta: snapshot.healthOk
        ? "admin-server 可正常响应运行时配置请求。"
        : "前台会优先进入 fallback-local 或接口失败态。",
      tone: snapshot.healthOk ? ("success" as const) : ("warning" as const),
    },
    {
      id: "data-source",
      icon: snapshot.dataSource === "mock" ? FiServer : FiDatabase,
      title: snapshot.dataSource === "mock" ? "业务 API 使用 Mock" : "业务 API 使用 BFF",
      meta:
        snapshot.dataSource === "mock"
          ? `${snapshot.mockRoutes} 个配置化 mock 端点参与本地联调。`
          : `真实 BFF 已启用，DB 状态：${snapshot.databaseStatus === "available" ? "可用" : "降级"}。`,
      tone: snapshot.dataSource === "mock" ? ("warning" as const) : ("success" as const),
    },
    {
      id: "observability",
      icon: snapshot.observabilityErrors > 0 ? FiAlertTriangle : FiActivity,
      title:
        snapshot.observabilityEvents > 0 || snapshot.observabilityErrors > 0
          ? "观测链路已有数据"
          : "等待前台观测上报",
      meta:
        snapshot.observabilityEvents > 0 || snapshot.observabilityErrors > 0
          ? `${snapshot.observabilityEvents} 个事件，${snapshot.observabilityErrors} 个错误。`
          : "启动 novel-rating 并刷新页面后应出现 page_view / Web Vitals。",
      tone:
        snapshot.observabilityErrors > 0
          ? ("warning" as const)
          : snapshot.observabilityEvents > 0
            ? ("success" as const)
            : ("brand" as const),
    },
  ];

  // build token 即将过期：snapshot 上携带数量 + 最早天数，告警 → 让 admin 提前
  // 30 天就开始考虑轮换；常态稳态时这条 notification 不会出现
  if (snapshot.buildTokensExpiringCount > 0) {
    const days = snapshot.buildTokensSoonestExpiringDays;
    const tone =
      days !== null && days <= 1
        ? ("warning" as const)
        : days !== null && days <= 7
          ? ("warning" as const)
          : ("brand" as const);
    allNotifications.push({
      id: "build-tokens-expiring",
      icon: FiAlertTriangle,
      title: `构建令牌即将过期`,
      meta:
        days !== null
          ? `${snapshot.buildTokensExpiringCount} 条 build token 进入告警窗口；最早 ${days} 天后失效，记得提前轮换。`
          : `${snapshot.buildTokensExpiringCount} 条 build token 进入告警窗口。`,
      tone,
    });
  }

  const notifications = allNotifications.filter((item) => !dismissedIds.has(item.id));

  const dismiss = (id: string) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      writeDismissedIds(next);
      return next;
    });
  };

  const dismissAll = () => {
    const next = new Set(allNotifications.map((item) => item.id));
    setDismissedIds(next);
    writeDismissedIds(next);
  };

  const restoreAll = () => {
    setDismissedIds(new Set());
    writeDismissedIds(new Set());
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="icon-button notification-button"
          type="button"
          aria-label="运行通知"
        >
          <FiBell aria-hidden="true" />
          {notifications.length > 0 && <span aria-hidden="true" />}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="notification-menu"
        side="bottom"
        align="end"
        showArrow
      >
        <div className="popover-title">
          <strong>运行通知</strong>
          <HStack gap="2" align="center">
            <Badge colorScheme="warning">{notifications.length}</Badge>
            {notifications.length > 0 ? (
              <button
                type="button"
                className="notification-bulk-action"
                onClick={dismissAll}
                aria-label="全部清除"
              >
                全部清除
              </button>
            ) : dismissedIds.size > 0 ? (
              <button
                type="button"
                className="notification-bulk-action"
                onClick={restoreAll}
                aria-label="恢复显示"
              >
                恢复显示
              </button>
            ) : null}
          </HStack>
        </div>
        <div className="notification-list">
          {notifications.length === 0 ? (
            <div className="notification-empty">没有需要关注的通知</div>
          ) : (
            notifications.map((item) => {
              const Icon = item.icon;
              return (
                <div className="notification-item" key={item.id}>
                  <span className={`notification-icon tone-${item.tone}`}>
                    <Icon aria-hidden="true" />
                  </span>
                  <span className="notification-body">
                    <strong>{item.title}</strong>
                    <small>{item.meta}</small>
                  </span>
                  <button
                    type="button"
                    className="notification-dismiss"
                    aria-label={`忽略 ${item.title}`}
                    onClick={() => dismiss(item.id)}
                  >
                    <FiX aria-hidden="true" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AccountPopover() {
  const user = getAdminUser();
  const displayName = user?.displayName ?? "平台管理员";
  const email = user?.email ?? user?.handle ?? "admin@novel-rating";
  const logout = () => {
    void logoutAdmin().finally(() => window.location.assign("/login"));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="account-trigger" type="button">
          <Avatar name={displayName} size="md" shape="circle" src={user?.avatarUrl ?? undefined} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="account-menu"
        side="bottom"
        align="end"
        showArrow
      >
        <div className="account-menu-head">
          <Avatar name={displayName} size="md" shape="circle" src={user?.avatarUrl ?? undefined} />
          <div>
            <strong>{displayName}</strong>
            <span>{email}</span>
          </div>
        </div>
        <Divider className="account-divider" />
        <Link to="/account" className="account-action">
          <FiUser aria-hidden="true" />
          账号与权限
        </Link>
        <Link to="/audit" className="account-action">
          <FiDatabase aria-hidden="true" />
          审计日志
        </Link>
        <button type="button" className="account-action account-logout" onClick={logout}>
          <FiLogOut aria-hidden="true" />
          退出登录
        </button>
      </PopoverContent>
    </Popover>
  );
}
